import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Switch,
  Spin,
  Empty,
  Tooltip,
  notification,
  Badge,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  ExportOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { renderOrgPaymentAdviceFromJob } from "../Organizations/generateOrgPaymentAdviceExport";
import {
  ORG_PAYMENT_ADVICE_EXPORT_TYPE,
  getOrgJobOutputFormat,
  isServerRenderedOutputFormat,
} from "./claimsExportHelpers";
import lib from "../../assets/utils/lib";
import { usePermissions } from "../../assets/utils/usePermissions";

const { Text } = Typography;

const STATUS_CONFIG = {
  queued: { color: "default", icon: <ClockCircleOutlined />, text: "Queued" },
  processing: { color: "processing", icon: <LoadingOutlined />, text: "Processing" },
  completed: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
  failed: { color: "error", icon: <CloseCircleOutlined />, text: "Failed" },
};

const EXPORT_TYPE_LABELS = {
  "admin.months": "Admin - Months",
  "admin.facilities": "Admin - Facilities",
  "admin.claims": "Admin - Claims",
  "payment.months": "Payment - Months",
  "payment.facilities": "Payment - Facilities",
  "payment.batches": "Payment - Batches",
  "payment.claims": "Payment - Claims",
  "payment.advice": "Payment - Payment Advice",
  "org.payment_advice": "Org - Utilization Report (CBA)",
  "claims.facility_encounter_summary": "Facility Encounter Summary",
};

const ClaimsExportsDrawer = ({ open, onClose }) => {
  const { hasAnyPermissionWithLegacy } = usePermissions();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const intervalRef = useRef(null);

  // Widens the export-job list to every user's jobs ("All users" switch).
  // Legacy super users keep it while their RBAC map is still empty.
  const canSeeAllExportJobs = hasAnyPermissionWithLegacy(["admin.*"], [1]);

  const fetchJobs = useCallback(async () => {
    try {
      const resp = await lib.listClaimsExportJobs(
        includeAll ? { include_all: 1 } : {}
      );
      const body = resp?.data;
      if (body?.error === 0 && Array.isArray(body?.data?.jobs || body?.data)) {
        const list = body.data.jobs || body.data;
        setJobs(list);
      }
    } catch (e) {
      // Silently fail on poll; user will see stale data
      console.error("Failed to fetch export jobs:", e?.message);
    }
  }, [includeAll]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchJobs().finally(() => setLoading(false));

    // Adaptive polling: every 5s for first 60s, then every 20s
    let pollCount = 0;
    const poll = () => {
      fetchJobs();
      pollCount += 1;
      const nextInterval = pollCount < 12 ? 5000 : 20000;
      intervalRef.current = setTimeout(poll, nextInterval);
    };
    intervalRef.current = setTimeout(poll, 5000);

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [open, fetchJobs]);

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingId(job.job_id);
    try {
      await lib.downloadClaimsExportJob(
        job.job_id,
        job.output_filename || "claims_export.csv"
      );
    } catch (e) {
      notification.error({
        message: "Download failed",
        description: e?.message || "Unable to download export file.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOrgPaymentAdviceDownload = async (job, format) => {
    if (!job?.job_id) return;
    setDownloadingId(job.job_id);
    setDownloadingFormat(format);
    try {
      await renderOrgPaymentAdviceFromJob(job.job_id, format);
      notification.success({
        message: "Download started",
        description: `Payment advice ${format.toUpperCase()} generated.`,
      });
    } catch (e) {
      notification.error({
        message: "Download failed",
        description: e?.message || "Unable to render payment advice export.",
      });
    } finally {
      setDownloadingId(null);
      setDownloadingFormat(null);
    }
  };

  const handleRetry = async (job) => {
    if (!job?.export_type || !job?.params) return;
    try {
      await lib.createClaimsExportJob(job.export_type, job.params);
      notification.success({
        message: "Export re-queued",
        description: "A new export job has been submitted.",
      });
      fetchJobs();
    } catch (e) {
      notification.error({
        message: "Retry failed",
        description: e?.message || "Unable to retry export.",
      });
    }
  };

  const columns = [
    {
      title: "Job ID",
      dataIndex: "job_id",
      key: "job_id",
      width: 180,
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v}>
          <Text code style={{ fontSize: 12 }}>
            {v?.slice(0, 20)}...
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Type",
      dataIndex: "export_type",
      key: "export_type",
      width: 200,
      ellipsis: true,
      render: (v, record) => {
        const label =
          record?.report_metadata?.label ||
          EXPORT_TYPE_LABELS[v] ||
          v;
        return (
          <Tooltip title={label}>
            <span>{EXPORT_TYPE_LABELS[v] || v}</span>
            {record?.report_metadata?.label ? (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {record.report_metadata.label}
                </Text>
              </div>
            ) : null}
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (v) => {
        const cfg = STATUS_CONFIG[v] || STATUS_CONFIG.queued;
        return (
          <Tag color={cfg.color} icon={cfg.icon}>
            {cfg.text}
          </Tag>
        );
      },
    },
    {
      title: "Requested",
      dataIndex: "requested_at",
      key: "requested_at",
      width: 160,
      render: (v) =>
        v ? new Date(v).toLocaleString() : "--",
    },
    {
      title: "Size / Rows",
      key: "size",
      width: 120,
      render: (_, record) => {
        const size = record.output_size_bytes;
        const rows = record.rows_exported;
        if (!size && !rows) return "--";
        const sizeText = size
          ? size < 1024
            ? `${size} B`
            : size < 1024 * 1024
            ? `${(size / 1024).toFixed(1)} KB`
            : `${(size / (1024 * 1024)).toFixed(1)} MB`
          : null;
        return (
          <span>
            {sizeText || ""}
            {sizeText && rows ? " / " : ""}
            {rows ? `${rows} rows` : ""}
          </span>
        );
      },
    },
    {
      title: "Requester",
      dataIndex: "requested_by_email",
      key: "requested_by_email",
      width: 180,
      ellipsis: true,
      render: (v) => v || "--",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const orgFormat = getOrgJobOutputFormat(record);
        const serverRendered =
          record.export_type === ORG_PAYMENT_ADVICE_EXPORT_TYPE &&
          isServerRenderedOutputFormat(orgFormat);

        return (
        <Space size="small">
          {record.status === "completed" && serverRendered ? (
            <Tooltip title={`Download ${orgFormat.toUpperCase()}`}>
              <Button
                type="primary"
                size="small"
                icon={orgFormat === "pdf" ? <FilePdfOutlined /> : <FileExcelOutlined />}
                loading={downloadingId === record.job_id}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          ) : null}
          {record.status === "completed" &&
          record.export_type === ORG_PAYMENT_ADVICE_EXPORT_TYPE &&
          !serverRendered ? (
            <>
              <Tooltip title="Render Excel (client)">
                <Button
                  type="primary"
                  size="small"
                  icon={<FileExcelOutlined />}
                  loading={downloadingId === record.job_id && downloadingFormat === "xlsx"}
                  onClick={() => handleOrgPaymentAdviceDownload(record, "xlsx")}
                />
              </Tooltip>
              <Tooltip title="Render PDF (client)">
                <Button
                  size="small"
                  icon={<FilePdfOutlined />}
                  loading={downloadingId === record.job_id && downloadingFormat === "pdf"}
                  onClick={() => handleOrgPaymentAdviceDownload(record, "pdf")}
                />
              </Tooltip>
            </>
          ) : null}
          {record.status === "completed" && record.export_type !== ORG_PAYMENT_ADVICE_EXPORT_TYPE ? (
            <Tooltip title="Download">
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                loading={downloadingId === record.job_id}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          ) : null}
          {record.status === "failed" && (
            <Tooltip title="Retry">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(record)}
              />
            </Tooltip>
          )}
          {record.status === "queued" || record.status === "processing" ? (
            <Spin size="small" />
          ) : null}
        </Space>
        );
      },
    },
  ];

  const hasActiveJobs = jobs.some(
    (j) => j.status === "queued" || j.status === "processing"
  );

  return (
    <Drawer
      title={
        <Space>
          <ExportOutlined />
          <span>Claims Exports</span>
          {hasActiveJobs && <Badge status="processing" />}
        </Space>
      }
      placement="right"
      width={900}
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text type="secondary">
          {hasActiveJobs
            ? "Some exports are still processing. The list refreshes automatically."
            : `${jobs.length} export job(s) found.`}
        </Text>
        {canSeeAllExportJobs && (
          <Space>
            <Text type="secondary">All users</Text>
            <Switch
              checked={includeAll}
              onChange={(checked) => {
                setIncludeAll(checked);
                setLoading(true);
                fetchJobs().finally(() => setLoading(false));
              }}
            />
          </Space>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={jobs}
        rowKey="job_id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: 900 }}
        locale={{
          emptyText: (
            <Empty
              description="No export jobs yet. Use Export CSV from claims drilldowns or queue organization utilization reports from Organizations."
            />
          ),
        }}
      />
    </Drawer>
  );
};

export default ClaimsExportsDrawer;
